<script lang="ts">
	import { Card, Button, Badge, Table, TableHead, TableBody, TableHeadCell, TableBodyRow, TableBodyCell, Select, Input, Heading } from 'flowbite-svelte';
	import AbstractID from '../../AbstractID';
	import site from '../../site';
	import { onMount } from 'svelte';
	import type { AuditLog } from '../../AppTypes';

	type BadgeColor = 'red' | 'yellow' | 'green' | 'indigo' | 'purple' | 'pink' | 'blue' | 'dark' | 'primary' | 'none';

	let logs: AuditLog[] = [];
	let loading = true;
	let error = '';
	let total = 0;
	let page = 1;
	let limit = 25;
	let actionFilter = '';
	let searchQuery = '';

	const actionTypes = [
		{ value: '', name: 'All Actions' },
		{ value: 'USER_REGISTERED', name: 'User Registered' },
		{ value: 'USER_LOGIN', name: 'User Login' },
		{ value: 'ROLE_CHANGED', name: 'Role Changed' },
		{ value: 'SETTINGS_CHANGED', name: 'Settings Changed' },
		{ value: 'USER_UPDATED', name: 'User Updated' }
	];

	const actionColors: Record<string, BadgeColor> = {
		'USER_REGISTERED': 'green',
		'USER_LOGIN': 'blue',
		'ROLE_CHANGED': 'yellow',
		'SETTINGS_CHANGED': 'purple',
		'USER_UPDATED': 'red'
	};

	const actionIcons: Record<string, string> = {
		'USER_REGISTERED': 'fa-user-plus',
		'USER_LOGIN': 'fa-right-to-bracket',
		'ROLE_CHANGED': 'fa-user-shield',
		'SETTINGS_CHANGED': 'fa-gear',
		'USER_UPDATED': 'fa-user-pen'
	};

	onMount(async () => {
		await loadLogs();
	});

	async function loadLogs() {
		loading = true;
		error = '';

		try {
			const offset = (page - 1) * limit;
			const params = new URLSearchParams({
				limit: limit.toString(),
				offset: offset.toString()
			});
			
			if (actionFilter) {
				params.append('action', actionFilter);
			}

			const response = await AbstractID.authenticatedFetch(`${site.backend.api.auditList}?${params}`);
			
			if (response.ok) {
				const data = await response.json();
				logs = data.items || [];
				total = data.total || 0;
			} else {
				throw new Error('Failed to fetch audit logs');
			}
		} catch (err: unknown) {
			error = err instanceof Error ? err.message : 'Failed to load audit logs';
			console.error('Audit logs error:', err);
		} finally {
			loading = false;
		}
	}

	function handleFilterChange() {
		page = 1;
		loadLogs();
	}

	function handlePageChange(newPage: number) {
		page = newPage;
		loadLogs();
	}

	function formatDate(dateString: string) {
		const date = new Date(dateString);
		return date.toLocaleString();
	}

	function formatRelativeTime(dateString: string) {
		const date = new Date(dateString);
		const now = new Date();
		const diff = now.getTime() - date.getTime();
		
		const minutes = Math.floor(diff / 60000);
		const hours = Math.floor(diff / 3600000);
		const days = Math.floor(diff / 86400000);

		if (minutes < 1) return 'just now';
		if (minutes < 60) return `${minutes}m ago`;
		if (hours < 24) return `${hours}h ago`;
		if (days < 7) return `${days}d ago`;
		return date.toLocaleDateString();
	}

	function parseDetails(details: unknown) {
		if (!details) return null;
		try {
			return typeof details === 'string' ? JSON.parse(details) : details;
		} catch {
			return details;
		}
	}

	$: totalPages = Math.ceil(total / limit);
	$: filteredLogs = searchQuery 
		? logs.filter(log => 
			log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
			log.ipAddress?.toLowerCase().includes(searchQuery.toLowerCase())
		)
		: logs;
</script>

<Heading tag="h3">Audit Logs</Heading>

<div class="space-y-6">
	<!-- Filters -->
	<Card class="max-w-none">
		<div class="flex flex-wrap items-center gap-4">
			<div class="flex-1 min-w-[200px]">
				<Input
					placeholder="Search logs..."
					bind:value={searchQuery}
				>
					<i slot="left" class="fa-solid fa-search text-gray-400"></i>
				</Input>
			</div>
			
			<div class="w-48">
				<Select 
					items={actionTypes} 
					bind:value={actionFilter}
					on:change={handleFilterChange}
					placeholder="Filter by action"
				/>
			</div>
			
			<Button color="alternative" on:click={loadLogs} disabled={loading}>
				{#if loading}
					<i class="fa-solid fa-spinner fa-spin me-2"></i>
				{:else}
					<i class="fa-solid fa-refresh me-2"></i>
				{/if}
				Refresh
			</Button>
		</div>
	</Card>

	{#if error}
		<div class="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>
			{error}
		</div>
	{/if}

	<!-- Logs Table -->
	<Card padding="none" class="max-w-none">
		{#if loading}
			<div class="p-8 text-center">
				<i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
				<p class="text-gray-500 dark:text-gray-400">Loading audit logs...</p>
			</div>
		{:else if filteredLogs.length === 0}
			<div class="p-8 text-center">
				<i class="fa-solid fa-clipboard-list text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">
					{searchQuery || actionFilter ? 'No logs match your filters' : 'No audit logs yet'}
				</p>
			</div>
		{:else}
			<Table striped={true} divClass="relative overflow-x-auto rounded-lg">
				<TableHead>
					<TableHeadCell class="w-36">Time</TableHeadCell>
					<TableHeadCell class="w-40">Action</TableHeadCell>
					<TableHeadCell class="w-32">Performed By</TableHeadCell>
					<TableHeadCell class="w-32">Target</TableHeadCell>
					<TableHeadCell>Details</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each filteredLogs as log}
						<TableBodyRow>
							<TableBodyCell>
								<div class="flex flex-col">
									<span class="text-sm font-medium text-gray-900 dark:text-white">
										{formatRelativeTime(log.creationDate)}
									</span>
									<span class="text-xs text-gray-500 dark:text-gray-400">
										{formatDate(log.creationDate)}
									</span>
								</div>
							</TableBodyCell>
							<TableBodyCell>
							<Badge color={(actionColors[log.action] || 'dark')}>
									<i class="fa-solid {actionIcons[log.action] || 'fa-circle'} me-1"></i>
									{log.action.replace(/_/g, ' ')}
								</Badge>
							</TableBodyCell>
							<TableBodyCell>
								<span class="text-sm text-gray-700 dark:text-gray-300">
								User #{log.userId || '—'}
							</span>
						</TableBodyCell>
						<TableBodyCell>
							{#if log.targetUserId}
								<span class="text-sm text-gray-700 dark:text-gray-300">
									User #{log.targetUserId}
									</span>
								{:else}
									<span class="text-sm text-gray-400">—</span>
								{/if}
							</TableBodyCell>
							<TableBodyCell>
								{#if log.details}
									{@const parsed = parseDetails(log.details)}
									{#if parsed}
										<code class="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded block max-w-[200px] truncate" title={typeof parsed === 'object' ? JSON.stringify(parsed) : String(parsed)}>
											{typeof parsed === 'object' ? JSON.stringify(parsed) : parsed}
										</code>
									{:else}
										<span class="text-sm text-gray-400">—</span>
									{/if}
								{:else}
									<span class="text-sm text-gray-400">—</span>
								{/if}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>

			<!-- Pagination -->
			{#if totalPages > 1}
				<div class="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
					<p class="text-sm text-gray-500 dark:text-gray-400">
						Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
					</p>
					
					<div class="flex items-center gap-2">
						<Button 
							color="alternative" 
							size="sm"
							disabled={page <= 1}
							on:click={() => handlePageChange(page - 1)}
						>
							<i class="fa-solid fa-chevron-left me-1"></i>
							Previous
						</Button>
						
						<span class="text-sm text-gray-600 dark:text-gray-400 px-3">
							Page {page} of {totalPages}
						</span>
						
						<Button 
							color="alternative" 
							size="sm"
							disabled={page >= totalPages}
							on:click={() => handlePageChange(page + 1)}
						>
							Next
							<i class="fa-solid fa-chevron-right ms-1"></i>
						</Button>
					</div>
				</div>
			{/if}
		{/if}
	</Card>

	<!-- Legend -->
	<Card class="max-w-none">
		<h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Action Types</h4>
		<div class="flex flex-wrap gap-3">
			{#each actionTypes.slice(1) as action}
				<Badge color={(actionColors[action.value] || 'dark')}>
					<i class="fa-solid {actionIcons[action.value] || 'fa-circle'} me-1"></i>
					{action.name}
				</Badge>
			{/each}
		</div>
	</Card>
</div>
